<script setup lang="ts">
import { computed, inject, ref, watch, type PropType, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useQuery, useMutation, keepPreviousData } from '@tanstack/vue-query';
import * as yup from 'yup';
import { caregiversApi, type CaregiverFilter } from '@/api/caregivers';
import { patientsApi, type PatientFilter } from '@/api/patients';
import {
  shiftsApi,
  type Shift,
  type ShiftForm,
} from '@/api/shifts';
import type { AxiosError } from 'axios';
import { transformShiftToShiftBatchCreateForm } from '@/utils/shifts-functions';
import { type SerializedUser } from '@/api/users';
import PriceInput from '@/components/price-input.vue';
import TimeInput from '@/components/time-input.vue';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import ComplianceCheckModal from '@/components/shifts/compliance-check-modal.vue';
import ShiftConflictDetector from '@/components/shifts/shift-conflict-detector.vue';
import { usePricingStructure } from '@/composables/usePricingStructure';
import { usePermissions } from '@/composables/usePermissions';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';
import { holidayMultipliersApi } from '@/api/holiday-multipliers';
import { useGroupFilterStore } from '@/stores/group-filter';
import ShiftsInfoBanner from '@/components/shifts/shifts-info-banner.vue';
import CreateKindToggle from '@/components/shifts/schedule-blocks/create-kind-toggle.vue';
import { useTableFilter } from '@/composables/useTableFilter';
import { memberWithoutGroups, resolveMemberGroupIds } from '@/utils/group-scope';
import { requestErrorMessage } from '@/utils/api-errors';
import { MINUTES_PER_HOUR, addMinutesToTime, endTimeFollowingStart } from '@/utils/time-of-day';

const { t } = useI18n();

const nameFilter = useTableFilter(['fullName']);

const emit = defineEmits(['close', 'refetch', 'success', 'switchToBlock']);

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const { hasPermission } = usePermissions();
const { trackShiftEvent } = useAnalytics();

const props = defineProps({
  shift: {
    type: Object as PropType<Shift> || undefined,
    default: undefined,
  },
  closeButtonText: {
    type: String,
    default: 'Cerrar',
  },
  mode: {
    type: String as PropType<'new' | 'edit'>,
    default: 'edit',
  },
  date: {
    type: String,
    required: true,
  },
  source: {
    type: String as PropType<'calendar' | 'date' | 'patient_profile'>,
    default: 'calendar',
  },
  // Offers the "Turno | Bloqueo" switch when the calendar has a patient context.
  showBlockToggle: {
    type: Boolean,
    default: false,
  },
  // Narrows the caregiver picker to these careers (a coverage alert's slot); empty = all.
  caregiverCareers: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

function getShiftType(startTime: string, endTime: string): string {
  if (startTime === '08:00' && endTime === '20:00') {
    return 'day';
  } else if (startTime === '20:00' && endTime === '08:00') {
    return 'night';
  }

  return 'custom';
}

const commonValidationSchema = {
  date: yup.string().required(t('shiftForm.fieldRequired')),
  caregiverId: yup.string().required(t('shiftForm.fieldRequired')),
  withTaxBill: hasPermission.value('shifts', 'read_financial_information') ?
    yup.boolean().required(t('shiftForm.fieldRequired')) : yup.boolean(),
  shiftType: yup.string().required(t('shiftForm.fieldRequired')),
  startTime: yup.string().required(t('shiftForm.fieldRequired')),
  endTime: yup.string().required(t('shiftForm.fieldRequired')),
};

const financialNumberField = hasPermission.value('shifts', 'read_financial_information') ?
  yup.number().required(t('shiftForm.fieldRequired'))
    .typeError(t('shiftForm.fieldRequired')) : yup.number();

const normalValidationSchema = yup.object({
  ...commonValidationSchema,
  caregiverCost: financialNumberField,
  price: financialNumberField,
});

const perHourValidationSchema = yup.object({
  ...commonValidationSchema,
  caregiverCostPerHour: financialNumberField,
  pricePerHour: financialNumberField,
});

const validationSchema = currentUser?.value.companyChargesAndPaysPerHours ?
  perHourValidationSchema : normalValidationSchema;

function isWeekend(dateInput: string = props.date) {
  const SATURDAY = 6;
  const SUNDAY = 0;
  const dateString = dateInput.split('T')[0] ?? dateInput;
  const date = new Date(dateString.replace(/-/g, '/'));

  return date.getDay() === SATURDAY || date.getDay() === SUNDAY;
}

const groupFilter = useGroupFilterStore();
const isCreating = computed(() => props.mode === 'new');
const groupsEnabled = computed(() => !!currentUser?.value?.companyGroupsEnabled);

const editModeInitialPricing = {
  withTaxBill: props.shift?.withTaxBill ?? false,
  weekCost: props.shift?.caregiverCost ?? 0,
  weekPrice: props.shift?.price ?? 0,
  weekCostPerHour: props.shift?.caregiverCostPerHour ?? 0,
  weekPricePerHour: props.shift?.pricePerHour ?? 0,
  weekendCost: props.shift?.caregiverCost ?? 0,
  weekendPrice: props.shift?.price ?? 0,
  weekendCostPerHour: props.shift?.caregiverCostPerHour ?? 0,
  weekendPricePerHour: props.shift?.pricePerHour ?? 0,
};

const initialPricing = props.mode === 'edit' ? editModeInitialPricing : {
  withTaxBill: false,
  weekCost: 0,
  weekPrice: 0,
  weekCostPerHour: 0,
  weekPricePerHour: 0,
  weekendCost: 0,
  weekendPrice: 0,
  weekendCostPerHour: 0,
  weekendPricePerHour: 0,
};

const commonInitialValues = {
  patientId: props.shift?.patientId,
  caregiverId: props.shift?.caregiverId,
  withTaxBill: initialPricing?.withTaxBill,
  shiftType: props.shift?.startTime ? getShiftType(props.shift.startTime, props.shift.endTime) : undefined,
  startTime: props.shift?.startTime ?? undefined,
  endTime: props.shift?.endTime ?? undefined,
  date: props.date.split('T')[0],
};

const fixedRateInitialValues = {
  ...commonInitialValues,
  caregiverCost: (isWeekend() ? initialPricing?.weekendCost : initialPricing?.weekCost) || 0,
  price: (isWeekend() ? initialPricing?.weekendPrice : initialPricing?.weekPrice) || 0,
};

const perHourRateInitialValues = {
  ...commonInitialValues,
  caregiverCostPerHour: (isWeekend() ? initialPricing?.weekendCostPerHour : initialPricing?.weekCostPerHour) || 0,
  pricePerHour: (isWeekend() ? initialPricing?.weekendPricePerHour : initialPricing?.weekPricePerHour) || 0,
};

const { handleSubmit, values, errors, defineField, setFieldValue } = useForm<ShiftForm>({
  validationSchema,
  initialValues: currentUser?.value.companyChargesAndPaysPerHours ? perHourRateInitialValues : fixedRateInitialValues,
});

const [patientId, patientIdAttrs] = defineField('patientId');
const [caregiverId, caregiverIdAttrs] = defineField('caregiverId');
const [caregiverCost, caregiverCostAttrs] = defineField('caregiverCost');
const [price, priceAttrs] = defineField('price');
const [caregiverCostPerHour, caregiverCostPerHourAttrs] = defineField('caregiverCostPerHour');
const [pricePerHour, pricePerHourAttrs] = defineField('pricePerHour');
const [withTaxBill, withTaxBillAttrs] = defineField('withTaxBill');
const [shiftType, shiftTypeAttrs] = defineField('shiftType');
const [startTime] = defineField('startTime');
const [endTime] = defineField('endTime');
const [shiftDate, shiftDateAttrs] = defineField('date');

const effectiveDate = computed(() => shiftDate.value || props.date);

const isMarkedShift = computed(() => !!props.shift?.checkIn);

const caregiverPickerGroupIds = ref<number[]>([]);
const patientPickerGroupIds = ref<number[]>([]);

const { data: caregivers, isFetching: isFetchingCaregivers } = useQuery({
  queryKey: ['caregiversForFilters', caregiverPickerGroupIds],
  queryFn: () => caregiversApi.indexForFilters(caregiverPickerGroupIds.value),
  placeholderData: keepPreviousData,
});
const { data: patients, isFetching: isFetchingPatients } = useQuery({
  queryKey: ['patientsForFilters', patientPickerGroupIds],
  queryFn: () => patientsApi.indexForFilters(patientPickerGroupIds.value),
  placeholderData: keepPreviousData,
});

watch(
  [patientId, () => groupFilter.groupIds, patients, groupsEnabled],
  () => {
    caregiverPickerGroupIds.value = groupsEnabled.value ?
      resolveMemberGroupIds(patientId.value, patients.value, groupFilter.groupIds ?? []) :
      [];
  },
  { immediate: true },
);

watch(
  [caregiverId, () => groupFilter.groupIds, caregivers, groupsEnabled],
  () => {
    patientPickerGroupIds.value = groupsEnabled.value ?
      resolveMemberGroupIds(caregiverId.value, caregivers.value, groupFilter.groupIds ?? []) :
      [];
  },
  { immediate: true },
);

watch(caregivers, (newList, oldList) => {
  if (oldList === undefined || !isCreating.value) return;
  if (!newList || !caregiverId.value) return;
  if (!newList.some((c: CaregiverFilter) => c.id === caregiverId.value)) {
    setFieldValue('caregiverId', undefined as never);
  }
});

watch(patients, (newList, oldList) => {
  if (oldList === undefined || !isCreating.value) return;
  if (!newList || !patientId.value) return;
  if (!newList.some((p: PatientFilter) => p.id === patientId.value)) {
    setFieldValue('patientId', undefined as never);
  }
});

interface ComplianceCheck {
  caregiverDisplayName: string;
  complianceCheck: {
    firstRuleText: string;
    secondRuleText: string;
    thirdRuleText: string;
  };
}

const complianceChecks = ref<ComplianceCheck[]>([]);
const showComplianceModal = ref(false);
const submitButtonDisabled = ref(false);
const conflictDetector = ref<InstanceType<typeof ShiftConflictDetector>>();

const showPricingUpdateDialog = ref(false);
interface PendingPricingUpdate {
  price: number;
  caregiverCost: number;
  pricePerHour: number;
  caregiverCostPerHour: number;
  withTaxBill: boolean;
}
const pendingPricingUpdate = ref<PendingPricingUpdate | null>(null);

function getAnalyticsEventType(): string {
  switch (props.source) {
  case 'calendar':
    return AnalyticsEvents.shifts.createdIndividualCalendar;
  case 'date':
    return AnalyticsEvents.shifts.createdIndividualDate;
  default:
    return AnalyticsEvents.shifts.createdIndividualCalendar;
  }
}

const {
  mutate: createShift, isError: isCreateError, error: createError,
} = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => {
      const shift = transformShiftToShiftBatchCreateForm(values);

      return shiftsApi.create(shift);
    },
    // The dialog stays open after a failed save so the user can act on the message
    // (e.g. pick another caregiver) and submit again, as in the batch form.
    onError: () => {
      submitButtonDisabled.value = false;
    },
    onSuccess: (data) => {
      trackShiftEvent(getAnalyticsEventType(), {
        patientId: values.patientId,
        caregiverId: values.caregiverId,
      });

      const responseData = data as { meta?: { complianceChecks?: ComplianceCheck[] } };
      if (responseData?.meta?.complianceChecks && responseData.meta.complianceChecks.length > 0) {
        complianceChecks.value = responseData.meta.complianceChecks;
        showComplianceModal.value = true;
      } else {
        emit('refetch');
        emit('success');
        emit('close');
      }
    },
  },
);

function handleConflictCancel() {
  submitButtonDisabled.value = false;
}

const {
  mutate: updateShift, isError: isUpdateError, error: updateError,
} = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => shiftsApi.update({ ...values, id: props.shift?.id }),
    onError: () => {
      submitButtonDisabled.value = false;
    },
    onSuccess: () => {
      emit('refetch');
      emit('success');
      emit('close');
    },
  },
);

function handleConfirm() {
  if (props.mode === 'edit') {
    updateShift();
  } else {
    createShift();
  }
}

const onSubmit = handleSubmit(() => {
  submitButtonDisabled.value = true;
  if (props.mode === 'edit') {
    conflictDetector.value?.checkConflicts({ ...values, id: props.shift?.id });
  } else {
    const shift = transformShiftToShiftBatchCreateForm(values);
    conflictDetector.value?.checkConflicts(shift);
  }
});

const currentCaregiver = computed<CaregiverFilter | undefined>(
  () => caregivers.value?.find((caregiver: CaregiverFilter) => caregiver.id === caregiverId.value),
);

const currentPatient = computed(
  () => patients.value?.find((patient: PatientFilter) => patient.id === patientId.value),
);

// With groups enabled a shift needs a group shared by patient and caregiver, so on create a
// member outside every group can be paired with nobody: the other picker offers nobody
// instead of the global filter's people, who would all fail on save. Edit mode keeps its
// pickers so older shifts stay editable; a failed save still explains itself below.
const patientWithoutGroups = computed(
  () => isCreating.value && groupsEnabled.value && memberWithoutGroups(currentPatient.value),
);
const caregiverWithoutGroups = computed(
  () => isCreating.value && groupsEnabled.value && memberWithoutGroups(currentCaregiver.value),
);
const caregiverOptions = computed(() => {
  if (patientWithoutGroups.value) return [];
  if (!props.caregiverCareers.length) return caregivers.value;

  return caregivers.value?.filter((caregiver: CaregiverFilter) => (
    props.caregiverCareers.includes(caregiver.career)
  ));
});
const patientOptions = computed(() => (caregiverWithoutGroups.value ? [] : patients.value));
const withoutGroupsMessage = computed(() => {
  if (patientWithoutGroups.value) return t('shiftForm.patientWithoutGroups');
  if (caregiverWithoutGroups.value) return t('shiftForm.caregiverWithoutGroups');

  return undefined;
});

// Immediate: the form often opens with both people prefilled from a calendar cell.
watch(patientWithoutGroups, (blocked) => {
  if (blocked && caregiverId.value) setFieldValue('caregiverId', undefined as never);
}, { immediate: true });

watch(caregiverWithoutGroups, (blocked) => {
  if (blocked && patientId.value) setFieldValue('patientId', undefined as never);
}, { immediate: true });

// The server's validation message (e.g. no shared group and how to fix it), else the generic one.
const saveErrorMessage = computed(
  () => requestErrorMessage(createError.value ?? updateError.value, t('userSession.defaultError')),
);

const currentPatientIdRef = computed(() => currentPatient.value?.id);
const currentCaregiverIdRef = computed(() => currentCaregiver.value?.id);

const canReadFinancialInfo = computed(() => hasPermission.value('shifts', 'read_financial_information'));

const caregiverHasActiveSalary = computed(
  () => currentCaregiver.value?.contractedWithSalary ?? false,
);

const shiftDateString = computed(() => props.date.split('T')[0]);

const { data: holidayCheck } = useQuery({
  queryKey: ['holidayCheck', shiftDateString],
  queryFn: () => holidayMultipliersApi.showForDate(shiftDateString.value!),
  enabled: computed(() => !!shiftDateString.value && canReadFinancialInfo.value),
});

const hasHolidaySurcharge = computed(() => {
  if (!holidayCheck.value) return false;

  return holidayCheck.value.priceMultiplier > 0 || holidayCheck.value.costMultiplier > 0;
});

const { pricingStructure: defaultPricing, isLoading: isPricingLoading } = usePricingStructure(
  currentPatientIdRef,
  currentCaregiverIdRef,
  canReadFinancialInfo,
);

const showPricingLoader = computed(
  () => isPricingLoading.value && props.mode === 'new' && !caregiverHasActiveSalary.value,
);

function updateShiftValuesForWeekend() {
  const defaultPricingData = defaultPricing.value ?? {};
  caregiverCost.value = defaultPricingData.weekendCost ?? 0;
  price.value = defaultPricingData.weekendPrice ?? 0;
  caregiverCostPerHour.value = defaultPricingData.weekendCostPerHour ?? 0;
  pricePerHour.value = defaultPricingData.weekendPricePerHour ?? 0;
}

function updateShiftValuesForWeek() {
  const defaultPricingData = defaultPricing.value ?? {};
  caregiverCost.value = defaultPricingData.weekCost ?? 0;
  price.value = defaultPricingData.weekPrice ?? 0;
  caregiverCostPerHour.value = defaultPricingData.weekCostPerHour ?? 0;
  pricePerHour.value = defaultPricingData.weekPricePerHour ?? 0;
}

// eslint-disable-next-line complexity
function getWeekdayPricing(): PendingPricingUpdate {
  const data = defaultPricing.value ?? {};
  return {
    price: data.weekPrice ?? 0,
    caregiverCost: data.weekCost ?? 0,
    pricePerHour: data.weekPricePerHour ?? 0,
    caregiverCostPerHour: data.weekCostPerHour ?? 0,
    withTaxBill: data.withTaxBill ?? false,
  };
}

// eslint-disable-next-line complexity
function getWeekendPricing(): PendingPricingUpdate {
  const data = defaultPricing.value ?? {};
  return {
    price: data.weekendPrice ?? 0,
    caregiverCost: data.weekendCost ?? 0,
    pricePerHour: data.weekendPricePerHour ?? 0,
    caregiverCostPerHour: data.weekendCostPerHour ?? 0,
    withTaxBill: data.withTaxBill ?? false,
  };
}

function getNewPricingValues(): PendingPricingUpdate {
  return isWeekend(effectiveDate.value) ? getWeekendPricing() : getWeekdayPricing();
}

function hasPricingChanged(newPricing: PendingPricingUpdate): boolean {
  if (newPricing.withTaxBill !== withTaxBill.value) return true;

  if (currentUser?.value.companyChargesAndPaysPerHours) {
    return newPricing.pricePerHour !== pricePerHour.value ||
           newPricing.caregiverCostPerHour !== caregiverCostPerHour.value;
  }

  return newPricing.price !== price.value ||
         newPricing.caregiverCost !== caregiverCost.value;
}

function checkForPricingUpdate() {
  if (!canReadFinancialInfo.value) return;
  if (!currentPatientIdRef.value || !currentCaregiverIdRef.value) return;

  const newPricing = getNewPricingValues();

  if (hasPricingChanged(newPricing)) {
    pendingPricingUpdate.value = newPricing;
    showPricingUpdateDialog.value = true;
  }
}

function acceptPricingUpdate() {
  if (!pendingPricingUpdate.value) return;

  const newPricing = pendingPricingUpdate.value;
  price.value = newPricing.price;
  caregiverCost.value = newPricing.caregiverCost;
  pricePerHour.value = newPricing.pricePerHour;
  caregiverCostPerHour.value = newPricing.caregiverCostPerHour;
  withTaxBill.value = newPricing.withTaxBill;

  showPricingUpdateDialog.value = false;
  pendingPricingUpdate.value = null;
}

function declinePricingUpdate() {
  showPricingUpdateDialog.value = false;
  pendingPricingUpdate.value = null;
}

function updateShiftValues() {
  if (props.mode === 'edit') return;

  if (isWeekend(effectiveDate.value)) {
    updateShiftValuesForWeekend();
  } else {
    updateShiftValuesForWeek();
  }
  withTaxBill.value = defaultPricing.value?.withTaxBill ?? false;
}

// A new custom shift opens as a one-hour range from the start it already has.
function openCustomRange() {
  if (isCreating.value && startTime.value) {
    endTime.value = addMinutesToTime(startTime.value, MINUTES_PER_HOUR);
  }
}

watch(shiftType, () => {
  if (shiftType.value === 'day') {
    startTime.value = '08:00';
    endTime.value = '20:00';
  } else if (shiftType.value === 'night') {
    startTime.value = '20:00';
    endTime.value = '08:00';
  } else if (shiftType.value === '24') {
    endTime.value = startTime.value;
  } else if (shiftType.value === 'custom') {
    openCustomRange();
  }
});
// While creating a custom shift the end follows the start (see endTimeFollowingStart); an
// existing shift being edited keeps the end the user sees until they change it.
watch(startTime, (newStart, previousStart) => {
  if (shiftType.value === '24') {
    endTime.value = startTime.value;
  } else if (shiftType.value === 'custom' && isCreating.value) {
    endTime.value = endTimeFollowingStart(previousStart, newStart, endTime.value);
  }
});

const titleDate = computed(() => {
  const date = props.date.split('T')[0] ?? props.date;

  return date.split('-').reverse().join('/');
});

const hasInitializedPricing = ref(false);

function handlePricingChange(newPricing: typeof defaultPricing.value) {
  if (props.mode === 'new') {
    updateShiftValues();
    return;
  }

  const shouldCheckForUpdate = props.mode === 'edit' && hasInitializedPricing.value && newPricing;
  if (shouldCheckForUpdate) {
    checkForPricingUpdate();
  }
}

watch([defaultPricing, isPricingLoading], ([newPricing, loading]) => {
  if (loading) return;

  handlePricingChange(newPricing);

  if (!hasInitializedPricing.value && newPricing) {
    hasInitializedPricing.value = true;
  }
}, { immediate: true });

function closeComplianceModal() {
  showComplianceModal.value = false;
  emit('refetch');
  emit('success');
  emit('close');
}
</script>

<template>
  <v-card
    v-if="hasPermission('shifts', 'create') || hasPermission('shifts', 'update')"
    class="w-100 pa-4"
    flat
  >
    <v-card-title class="mb-2 d-flex align-center ga-2">
      {{ props.mode === 'edit' ? t('shiftForm.editTitle') : t('shiftForm.newTitle') }} {{ titleDate }}
      <v-spacer />
      <create-kind-toggle
        v-if="showBlockToggle && props.mode === 'new'"
        model-value="shift"
        @update:model-value="$event === 'block' && emit('switchToBlock')"
      />
    </v-card-title>
    <!-- flex-0-0: the dialog card lays its children out as a flex column and
         the alert gets a zero flex basis there, so it collapsed to its grid
         minimum and rendered clipped whenever the form outgrew the viewport. -->
    <shifts-info-banner
      v-if="isCreating"
      class="mx-2 flex-0-0"
    />
    <v-form
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <!-- Compact fields without a reserved messages area: the dialog has to
           fit a laptop viewport (~600 px) without scrolling, even with the
           custom time row and the financial block. hide-details="auto" still
           shows errors and persistent hints. -->
      <v-card flat>
        <v-row class="mx-2 mt-2 d-flex">
          <v-autocomplete
            v-model="patientId"
            v-bind="patientIdAttrs"
            class="px-2 v-col-12 v-col-sm-6"
            variant="outlined"
            density="compact"
            hide-details="auto"
            item-title="fullName"
            item-value="id"
            clearable
            :label="t('shift.patient')"
            :error-messages="errors.patientId"
            :items="patientOptions"
            :loading="isFetchingPatients"
            :disabled="caregiverWithoutGroups"
            :custom-filter="nameFilter"
          />
          <v-autocomplete
            v-model="caregiverId"
            v-bind="caregiverIdAttrs"
            class="px-2 v-col-12 v-col-sm-6"
            variant="outlined"
            density="compact"
            hide-details="auto"
            item-title="fullName"
            item-value="id"
            clearable
            :label="t('shift.caregiver')"
            :error-messages="errors.caregiverId"
            :items="caregiverOptions"
            :loading="isFetchingCaregivers"
            :disabled="patientWithoutGroups"
            :custom-filter="nameFilter"
          />
        </v-row>
        <v-alert
          v-if="withoutGroupsMessage"
          class="mx-4 mt-2 member-without-groups-alert"
          density="compact"
          type="warning"
          variant="tonal"
        >
          {{ withoutGroupsMessage }}
        </v-alert>
        <v-row class="mx-2 d-flex">
          <v-select
            v-model="shiftType"
            v-bind="shiftTypeAttrs"
            :label="t('shiftForm.shiftTypeTitle')"
            :error-messages="errors.shiftType"
            :items="[
              { title: t('shiftForm.shiftTypeDay'), value: 'day' },
              { title: t('shiftForm.shiftTypeNight'), value: 'night' },
              { title: t('shiftForm.shiftType24'), value: '24' },
              { title: t('shiftForm.shiftTypeCustom'), value: 'custom' }
            ]"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="px-2 v-col-12 v-col-sm-6"
          />
          <v-text-field
            v-if="mode === 'edit'"
            v-model="shiftDate"
            v-bind="shiftDateAttrs"
            type="date"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="px-2 v-col-12 v-col-sm-6 shift-date-input"
            :label="t('shiftForm.dateTitle')"
            :disabled="isMarkedShift"
            :hint="isMarkedShift ? t('shiftForm.dateMarkedShiftInfo') : undefined"
            :persistent-hint="isMarkedShift"
            :error-messages="errors.date"
          />
        </v-row>
        <v-row
          v-if="shiftType === '24' || shiftType === 'custom'"
          class="mx-2 d-flex shift-time-row"
        >
          <time-input
            v-model="startTime"
            class="px-2 v-col-12 v-col-sm-6"
            label="Hora Inicio"
            hide-details="auto"
            :error-messages="errors.startTime"
          />
          <time-input
            v-if="shiftType === 'custom'"
            v-model="endTime"
            class="px-2 v-col-12 v-col-sm-6"
            label="Hora Término"
            hide-details="auto"
            :error-messages="errors.endTime"
          />
        </v-row>
        <template v-if="hasPermission('shifts', 'read_financial_information')">
          <v-alert
            v-if="hasHolidaySurcharge && mode === 'new'"
            type="info"
            variant="tonal"
            density="compact"
            class="mx-4 my-2"
          >
            Este turno cae en un día {{ holidayCheck?.humanHolidayType?.toLowerCase() }}.
            Se aplicará un recargo de
            <template v-if="holidayCheck?.priceMultiplier && holidayCheck?.costMultiplier">
              {{ holidayCheck.priceMultiplier }}% sobre el precio
              y {{ holidayCheck.costMultiplier }}% sobre el pago al profesional.
            </template>
            <template v-else-if="holidayCheck?.priceMultiplier">
              {{ holidayCheck.priceMultiplier }}% sobre el precio.
            </template>
            <template v-else>
              {{ holidayCheck?.costMultiplier }}% sobre el pago al profesional.
            </template>
          </v-alert>
          <v-row
            v-if="currentUser?.companyChargesAndPaysPerHours"
            class="mx-2 d-flex"
          >
            <v-skeleton-loader
              v-if="showPricingLoader"
              class="px-2 v-col-12 v-col-sm-6"
              type="text"
            />
            <price-input
              v-else
              v-model="pricePerHour"
              v-bind="pricePerHourAttrs"
              class="px-2 v-col-12 v-col-sm-6"
              density="compact"
              hide-details="auto"
              :label="t('shift.pricePerHour')"
              :error-messages="errors.pricePerHour"
            />
            <v-skeleton-loader
              v-if="showPricingLoader"
              class="px-2 v-col-12 v-col-sm-6"
              type="text"
            />
            <price-input
              v-else-if="!caregiverHasActiveSalary"
              v-model="caregiverCostPerHour"
              v-bind="caregiverCostPerHourAttrs"
              class="px-2 v-col-12 v-col-sm-6"
              density="compact"
              hide-details="auto"
              :label="t('shift.caregiverCostPerHour')"
              :error-messages="errors.caregiverCostPerHour"
            />
          </v-row>
          <v-row
            v-else
            class="mx-2 d-flex"
          >
            <v-skeleton-loader
              v-if="showPricingLoader"
              class="px-2 v-col-12 v-col-sm-6"
              type="text"
            />
            <price-input
              v-else
              v-model="price"
              v-bind="priceAttrs"
              class="px-2 v-col-12 v-col-sm-6"
              density="compact"
              hide-details="auto"
              :label="t('shift.price')"
              :error-messages="errors.price"
            />
            <v-skeleton-loader
              v-if="showPricingLoader"
              class="px-2 v-col-12 v-col-sm-6"
              type="text"
            />
            <price-input
              v-else-if="!caregiverHasActiveSalary"
              v-model="caregiverCost"
              v-bind="caregiverCostAttrs"
              class="px-2 v-col-12 v-col-sm-6"
              density="compact"
              hide-details="auto"
              :label="t('shift.caregiverCost')"
              :error-messages="errors.caregiverCost"
            />
          </v-row>
          <v-alert
            v-if="caregiverHasActiveSalary"
            type="info"
            variant="tonal"
            density="compact"
            class="mx-4 my-2"
          >
            {{ t('shiftForm.caregiverHasSalaryInfo') }}
          </v-alert>
          <template v-else>
            <v-skeleton-loader
              v-if="showPricingLoader"
              class="mx-4"
              type="text"
            />
            <v-radio-group
              v-else
              v-model="withTaxBill"
              v-bind="withTaxBillAttrs"
              :label="t('shiftBatchCreateForm.withTaxBillTitle')"
              :error-messages="errors.withTaxBill"
              class="mx-4 mt-2 tax-bill-radio-group"
              density="compact"
              hide-details="auto"
              inline
            >
              <v-radio
                :label="t('shiftBatchCreateForm.yes')"
                :value="true"
              />
              <v-radio
                :label="t('shiftBatchCreateForm.no')"
                :value="false"
              />
            </v-radio-group>
          </template>
        </template>
        <v-card-actions>
          <v-btn
            :text="props.closeButtonText"
            @click="$emit('close')"
          />
          <v-spacer />
          <v-btn
            v-if="mode === 'edit'"
            color="primary"
            type="submit"
            :disabled="submitButtonDisabled"
          >
            {{ t('shiftForm.editSubmit') }}
          </v-btn>
          <v-btn
            v-else-if="mode === 'new'"
            color="primary"
            type="submit"
            :disabled="submitButtonDisabled || showPricingLoader"
          >
            {{ t('shiftForm.newSubmit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <v-alert
      v-if="isCreateError || isUpdateError"
      class="mt-4 rounded flex-0-0"
      density="compact"
      :type="'error'"
    >
      {{ saveErrorMessage }}
    </v-alert>

    <compliance-check-modal
      v-model="showComplianceModal"
      :compliance-checks="complianceChecks"
      @update:model-value="closeComplianceModal"
    />

    <responsive-dialog
      v-model="showPricingUpdateDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ t('shiftForm.pricingUpdateTitle') }}
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            {{ t('shiftForm.pricingUpdateDescription') }}
          </p>
          <v-table
            v-if="pendingPricingUpdate"
            density="compact"
          >
            <thead>
              <tr>
                <th>{{ t('shiftForm.pricingUpdateField') }}</th>
                <th>{{ t('shiftForm.pricingUpdateCurrent') }}</th>
                <th>{{ t('shiftForm.pricingUpdateNew') }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="currentUser?.companyChargesAndPaysPerHours">
                <tr v-if="pendingPricingUpdate.pricePerHour !== pricePerHour">
                  <td>{{ t('shift.pricePerHour') }}</td>
                  <td>${{ pricePerHour?.toLocaleString('es-CL') }}</td>
                  <td class="text-primary font-weight-bold">
                    ${{ pendingPricingUpdate.pricePerHour.toLocaleString('es-CL') }}
                  </td>
                </tr>
                <tr v-if="pendingPricingUpdate.caregiverCostPerHour !== caregiverCostPerHour">
                  <td>{{ t('shift.caregiverCostPerHour') }}</td>
                  <td>${{ caregiverCostPerHour?.toLocaleString('es-CL') }}</td>
                  <td class="text-primary font-weight-bold">
                    ${{ pendingPricingUpdate.caregiverCostPerHour.toLocaleString('es-CL') }}
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr v-if="pendingPricingUpdate.price !== price">
                  <td>{{ t('shift.price') }}</td>
                  <td>${{ price?.toLocaleString('es-CL') }}</td>
                  <td class="text-primary font-weight-bold">
                    ${{ pendingPricingUpdate.price.toLocaleString('es-CL') }}
                  </td>
                </tr>
                <tr v-if="pendingPricingUpdate.caregiverCost !== caregiverCost">
                  <td>{{ t('shift.caregiverCost') }}</td>
                  <td>${{ caregiverCost?.toLocaleString('es-CL') }}</td>
                  <td class="text-primary font-weight-bold">
                    ${{ pendingPricingUpdate.caregiverCost.toLocaleString('es-CL') }}
                  </td>
                </tr>
              </template>
              <tr v-if="pendingPricingUpdate.withTaxBill !== withTaxBill">
                <td>{{ t('shiftBatchCreateForm.withTaxBillTitle') }}</td>
                <td>{{ withTaxBill ? t('shiftBatchCreateForm.yes') : t('shiftBatchCreateForm.no') }}</td>
                <td class="text-primary font-weight-bold">
                  {{ pendingPricingUpdate.withTaxBill ? t('shiftBatchCreateForm.yes') : t('shiftBatchCreateForm.no') }}
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            @click="declinePricingUpdate"
          >
            {{ t('shiftForm.pricingUpdateDecline') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="acceptPricingUpdate"
          >
            {{ t('shiftForm.pricingUpdateAccept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </responsive-dialog>

    <shift-conflict-detector
      ref="conflictDetector"
      :mode="mode === 'edit' ? 'update' : 'create'"
      @confirm="handleConfirm"
      @cancel="handleConflictCancel"
    />
  </v-card>
</template>
<style scoped>
/* The question and its Sí/No options on a single line (Vuetify stacks the label above). */
.tax-bill-radio-group :deep(> .v-input__control) {
  flex-flow: row wrap;
  align-items: center;
  column-gap: 8px;
}

.tax-bill-radio-group :deep(> .v-input__control > .v-label + .v-selection-control-group) {
  margin-top: 0;
}
</style>
